import { getCurrentInstance } from 'vue'

/**
 * Resolves the custom element host node from the current Vue component instance.
 * Must be called during `setup()` (synchronous) to capture the instance.
 *
 * Returns a function that dispatches CustomEvents from the host element,
 * safe to call from watchers, event handlers, or any later context.
 */
export function useWidgetEvents() {
    const instance = getCurrentInstance()
    const el = instance?.vnode?.el as Element | null | undefined

    function getHost(): Element | null {
        if (!el) {
            return null
        }
        const root = el.getRootNode()
        return root instanceof ShadowRoot ? root.host : el
    }

    /**
     * Dispatches a CustomEvent from the custom element's host node so it
     * bubbles out of the closed shadow DOM and is observable by the host page.
     */
    function emit(name: string, detail?: Record<string, unknown>): void {
        const host = getHost()
        if (!host) {
            return
        }
        host.dispatchEvent(
            new CustomEvent(name, {
                detail: detail ?? null,
                bubbles: true,
                composed: true,
            }),
        )
    }

    return { emit }
}
